import { create } from 'zustand';
import { UserProfile, CompanyInfo } from '@/types';
import { apiService } from '@/services/api.service';
import { AsyncState } from './types';

interface ProfileState extends AsyncState {
  // State
  azureProfile: UserProfile | null;
  companyInfo: CompanyInfo | null;
  isLoaded: boolean; // Track if profile has been loaded at least once
  
  // Actions
  fetchProfile: (accessToken: string) => Promise<void>;
  setProfile: (azureProfile: UserProfile, companyInfo?: CompanyInfo | null) => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  azureProfile: null,
  companyInfo: null,
  isLoaded: false,
  loading: false,
  error: null,
};

export const useProfileStore = create<ProfileState>((set, get) => ({
  ...initialState,

  /**
   * Fetch user profile from Azure AD and company info
   * Only fetches if not already loaded (acts as a singleton)
   */
  fetchProfile: async (accessToken: string) => {
    // Don't refetch if already loaded and no error
    const { isLoaded, loading } = get();
    if (isLoaded && !loading) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await apiService.getUserProfile(accessToken);
      
      set({
        azureProfile: response.data.azureProfile,
        companyInfo: response.data.companyInfo || null,
        isLoaded: true,
        loading: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch profile';
      set({ 
        error: errorMessage, 
        loading: false,
        isLoaded: false,
      });
      throw error;
    }
  },

  /**
   * Manually set profile data (useful for initial load from navigation state)
   */
  setProfile: (azureProfile: UserProfile, companyInfo: CompanyInfo | null = null) => {
    set({
      azureProfile,
      companyInfo,
      isLoaded: true,
      error: null,
    });
  },

  /**
   * Update specific fields of the Azure profile
   */
  updateProfile: (updates: Partial<UserProfile>) => {
    const { azureProfile } = get();
    if (azureProfile) {
      set({
        azureProfile: {
          ...azureProfile,
          ...updates,
        },
      });
    }
  },

  /**
   * Set error message manually
   */
  setError: (error: string | null) => {
    set({ error });
  },

  /**
   * Reset store to initial state (useful for logout)
   */
  reset: () => {
    set(initialState);
  },
}));
